import { ColDef } from 'ag-grid-community';
import { BudgetBys } from 'business/constants';
import { translateBlock, translateBlockValue } from 'i18n';
import { TFunction } from 'i18next';
import { ArgumentT } from 'routes/BudgetModule/config/types';

const DEFAULT_COL_DEF = {
    flex: 1,
    minWidth: 80,
    sortable: true,
    resizable: true,
    suppressMovable: true,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    floatingFilterComponent: 'textFloatingFilter',
    suppressFloatingFilterButton: true,
};

/**
 * Generates the column definitions for the arguments list grid in the AnalyseArguments view.
 *
 * @param firstBudgetBy - The budget ventilation used as the column's header name and value selector.
 * Only value of the same ventilation will be displayed.
 * @returns An array of column definitions for the AG Grid table.
 */
export const getArgumentsListColumnDefs = (
    firstBudgetBy: BudgetBys | string,
    onArgumentOpened: (data: ArgumentT) => void,
    t: TFunction,
    isSchool?: boolean,
): ColDef[] => {
    const linkStyle: React.CSSProperties = {
        fontWeight: '600',
        textDecoration: 'underline',
    };
    const columnDefs: ColDef[] = [
        {
            headerName: t('analyseArguments.argumentName'),
            field: 'title',
            cellRenderer: 'linkCellRenderer',
            cellRendererParams: {
                getUrl: (data: ArgumentT) =>
                    `/budget_proposal/${data.budgetProposal.id}/resource/${encodeURIComponent(data.resourceSoftwareId)}/arguments/${data.id}`,
                style: linkStyle,
                target: '_blank',
                onClick: onArgumentOpened,
            },
            ...DEFAULT_COL_DEF,
            flex: 2,
        },
        {
            headerName: t('analyseArguments.budgetName'),
            field: 'budgetProposal.budgetName',
            valueGetter: (params) => params.data.budgetProposal.budgetName,
            ...DEFAULT_COL_DEF,
        },
        {
            headerName: translateBlock(firstBudgetBy),
            field: 'resourceName',
            valueGetter: (params) =>
                // We remove the resource value to make it match with
                // the ventilation of the first bp (decided by product)
                firstBudgetBy === params.data.budgetProposal.by
                    ? params.data.resourceName
                    : '',
            ...DEFAULT_COL_DEF,
        },
    ];

    if (!isSchool) {
        columnDefs.push({
            headerName: t('Section'),
            field: 'budgetProposal.section',
            valueGetter: (params) =>
                translateBlockValue(params.data.budgetProposal.section, true),
            ...DEFAULT_COL_DEF,
        });
    }
    return columnDefs;
};
